import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Pencil, Trash2, Plus, X } from 'lucide-react';

export default function ManageSurveys() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    surveyURL: '',
    surveyResultURL: '',
    surveyQRImageURL: '',
    approximateTime: 0,
    questions: 0,
    responses: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'surveys'));
      const surveysData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSurveys(surveysData.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds));
    } catch (error) {
      console.error('Error fetching surveys:', error);
      alert('Failed to fetch surveys');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSurvey) {
        // Update existing survey
        const surveyRef = doc(db, 'surveys', editingSurvey.id);
        await updateDoc(surveyRef, {
          ...formData,
          approximateTime: parseInt(formData.approximateTime) || 0,
          questions: parseInt(formData.questions) || 0,
          responses: parseInt(formData.responses) || 0,
          updatedAt: serverTimestamp()
        });
        alert('Survey updated successfully!');
      } else {
        // Add new survey
        await addDoc(collection(db, 'surveys'), {
          ...formData,
          approximateTime: parseInt(formData.approximateTime) || 0,
          questions: parseInt(formData.questions) || 0,
          responses: parseInt(formData.responses) || 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('Survey added successfully!');
      }

      setFormData({
        title: '',
        surveyURL: '',
        surveyResultURL: '',
        surveyQRImageURL: '',
        approximateTime: 0,
        questions: 0,
        responses: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
      });
      setEditingSurvey(null);
      setShowForm(false);
      fetchSurveys();
    } catch (error) {
      console.error('Error saving survey:', error);
      alert('Failed to save survey');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (survey) => {
    setEditingSurvey(survey);
    setFormData({
      title: survey.title,
      surveyURL: survey.surveyURL || '',
      surveyResultURL: survey.surveyResultURL || '',
      surveyQRImageURL: survey.surveyQRImageURL || '',
      approximateTime: survey.approximateTime || '',
      questions: survey.questions || 0,
      responses: survey.responses || 0,
      startDate: survey.startDate?.split('T')[0] || new Date().toISOString().split('T')[0],
      endDate: survey.endDate?.split('T')[0] || new Date().toISOString().split('T')[0]
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;

    try {
      await deleteDoc(doc(db, 'surveys', id));
      alert('Survey deleted successfully!');
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      alert('Failed to delete survey');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSurvey(null);
    setFormData({
      title: '',
      surveyURL: '',
      surveyResultURL: '',
      surveyQRImageURL: '',
      approximateTime: 0,
      questions: 0,
      responses: 0,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    });
  };

  return (
    <div className="py-20 min-h-screen bg-gradient-to-b from-gray-900 to-black">
      <div className="container-width section-padding">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-white">Manage Surveys</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-2"
          >
            {showForm ? (
              <>
                <X className="w-5 h-5" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Add New Survey
              </>
            )}
          </button>
        </div>

        {showForm && (
          <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg mb-8 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingSurvey ? 'Edit Survey' : 'Add New Survey'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white mb-2">Survey Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="Enter survey title"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Survey URL *</label>
                <input
                  type="url"
                  required
                  value={formData.surveyURL}
                  onChange={(e) => setFormData({ ...formData, surveyURL: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="https://docs.google.com/forms/..."
                />
              </div>

              <div>
                <label className="block text-white mb-2">Survey Result URL</label>
                <input
                  type="url"
                  value={formData.surveyResultURL}
                  onChange={(e) => setFormData({ ...formData, surveyResultURL: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="https://docs.google.com/spreadsheets/... (optional)"
                />
              </div>

              <div>
                <label className="block text-white mb-2">Survey QR Code Image URL</label>
                <input
                  type="url"
                  value={formData.surveyQRImageURL}
                  onChange={(e) => setFormData({ ...formData, surveyQRImageURL: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="https://example.com/qr-code.png (optional)"
                />
                {formData.surveyQRImageURL && (
                  <div className="mt-3">
                    <p className="text-gray-400 text-sm mb-2">Preview:</p>
                    <img
                      src={formData.surveyQRImageURL}
                      alt="QR Code Preview"
                      className="max-w-xs rounded-lg border border-gray-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Approximate Time (minutes) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.approximateTime}
                    onChange={(e) => setFormData({ ...formData, approximateTime: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    placeholder="e.g., 8"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Number of Questions</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.questions}
                    onChange={(e) => setFormData({ ...formData, questions: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Number of Responses (Manual)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.responses}
                  onChange={(e) => setFormData({ ...formData, responses: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="0"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1"
                >
                  {loading ? 'Saving...' : editingSurvey ? 'Update Survey' : 'Add Survey'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Title</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Approximate Time</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Questions</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Responses</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Start Date</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">End Date</th>
                  <th className="px-6 py-4 text-right text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {surveys.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                      No surveys yet. Add your first survey!
                    </td>
                  </tr>
                ) : (
                  surveys.map((survey) => (
                    <tr key={survey.id} className="hover:bg-gray-900/30 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-white font-medium">{survey.title}</div>
                          {survey.surveyURL && (
                            <a
                              href={survey.surveyURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-400 text-sm hover:underline"
                            >
                              View Survey
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">{survey.approximateTime ? `~${survey.approximateTime} mins` : 'N/A'}</td>
                      <td className="px-6 py-4 text-gray-300">{survey.questions || 0}</td>
                      <td className="px-6 py-4 text-gray-300">{survey.responses || 0}</td>
                      <td className="px-6 py-4 text-gray-300">
                        {survey.startDate ? new Date(survey.startDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {survey.endDate ? new Date(survey.endDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(survey)}
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="Edit survey"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(survey.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete survey"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => navigate('/projects')}
            className="text-primary-400 hover:text-primary-300 transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
